import logo from '@/assets/hiredcoach-logo.svg';
import UserAvatar from '@/components/avatar/UserAvatar';
import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { JobApiResponse, PitchAnalysisResponse, useApiClient } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { BarChart3, Clock, ExternalLink, HelpCircle, MessageCircle, Plus } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

// Job Name Display Component
const JobNameDisplay: React.FC<{ jobId: string; demoMode?: boolean }> = ({ jobId, demoMode = false }) => {
    const { api } = useApiClient();
    const navigate = useNavigate();
    const [job, setJob] = useState<JobApiResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const demoModeRef = useRef(demoMode);

    // Update ref when demoMode changes
    useEffect(() => {
        demoModeRef.current = demoMode;
    }, [demoMode]);

    // Initialize demo job immediately if in demo mode
    useEffect(() => {
        if (demoMode) {
            setJob({
                id: jobId,
                title: 'Senior Product Manager',
                company: 'Acme Corp',
                description: '',
                location: 'Remote',
                remote: 'yes',
                salary: '',
                generation_status: 'ready',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                notes: [],
                statuses: [],
                persons: [],
                job_interviews: [],
                interviews: [],
            });
            setLoading(false);
            return;
        }
    }, [demoMode, jobId]);

    useEffect(() => {
        // If in demo mode, never make API calls
        if (demoMode || demoModeRef.current) {
            return;
        }

        const fetchJob = async () => {
            // Final check before making API call
            if (demoModeRef.current || demoMode) {
                setLoading(false);
                return;
            }
            
            try {
                const jobs = await api.getJobs();
                
                // Check again before processing response
                if (demoModeRef.current || demoMode) {
                    setLoading(false);
                    return;
                }
                
                const foundJob = jobs.find(j => j.id === jobId);
                setJob(foundJob || null);
            } catch (error) {
                console.error('Failed to fetch job:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchJob();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobId, demoMode]);

    if (loading) {
        return <span className="text-xs text-gray-500">Loading job...</span>;
    }

    if (!job) {
        return <span className="text-xs text-gray-500">Job not found</span>;
    }

    const jobName = job.title ? `${job.title} - ${job.company}` : job.company;

    return (
        <button
            onClick={(e) => {
                e.stopPropagation(); // Prevent card click
                if (!demoMode) {
                    navigate(`/jobs/${jobId}`);
                }
            }}
            className={`inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline transition-colors ${demoMode ? 'cursor-default' : ''}`}
            disabled={demoMode}
        >
            <span>Target: {jobName}</span>
            {!demoMode && <ExternalLink className="w-3 h-3" />}
        </button>
    );
};

interface PitchProps {
    demoMode?: boolean;
    embed?: boolean;
    demoPitches?: PitchAnalysisResponse[];
}

const Pitch: React.FC<PitchProps> = ({ demoMode = false, embed = false, demoPitches }) => {
    const navigate = useNavigate();
    const { api } = useApiClient();
    const { user } = useAuth();
    const [pitches, setPitches] = useState<PitchAnalysisResponse[]>([]);
    const [loading, setLoading] = useState(true);
    const [gradingPitches, setGradingPitches] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (demoMode) {
            // Use provided demo pitches or a minimal fallback
            if (demoPitches && demoPitches.length) {
                setPitches(demoPitches);
            } else {
                setPitches([]);
            }
            setLoading(false);
            return;
        }
        fetchPitches();
    }, []);

    const fetchPitches = async () => {
        try {
            setLoading(true);
            const pitchData = await api.getPitchAnalyses();
            setPitches(pitchData);
        } catch (error) {
            console.error('Failed to fetch pitches:', error);
            toast.error('Failed to load pitch analyses');
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (pitch: PitchAnalysisResponse) => {
        const status = pitch.status;
        
        switch (status.toLowerCase()) {
            case 'complete':
            case 'ready':
                // Show grade badge (no numeric score)
                const grade = pitch.overall_score;
                if (grade !== undefined && grade !== null) {
                    const label = getScoreLabel(grade);
                    const bgColor = getGradeBackgroundColor(grade);
                    return (
                        <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${bgColor}`}>
                            {label}
                        </div>
                    );
                } else {
                    // Fallback to Complete badge if scores aren't available
                    return <Badge className="bg-green-100 text-green-800 border-green-200">Complete</Badge>;
                }
            case 'grading':
                return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Grading</Badge>;
            case 'analyzing':
            case 'pending':
                return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Analyzing</Badge>;
            case 'error':
                return <Badge className="bg-red-100 text-red-800 border-red-200">Error</Badge>;
            default:
                return <Badge className="bg-gray-100 text-gray-800 border-gray-200">{status}</Badge>;
        }
    };

    const getTimeDisplay = (timeLimit: string) => {
        return `${timeLimit} seconds`;
    };

    const formatDate = (dateString: string) => {
        // Parse the UTC timestamp and convert to local time for display
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZoneName: 'short'
        });
    };

    const getFocusDisplay = (pitch: PitchAnalysisResponse) => {
        const pitchType = pitch.pitch_type || 'general';
        switch (pitchType) {
            case 'general':
                return 'You';
            case 'employment':
                return 'Employment';
            case 'education':
                return 'Education';
            default:
                return 'You';
        }
    };

    const handleCreatePitch = () => {
        if (demoMode) return;
        navigate('/pitch/new');
    };

    const handlePitchClick = (pitchId: string) => {
        if (demoMode) return;
        navigate(`/pitch/${pitchId}`);
    };

    // Grading helper functions (matching PitchAnalysis.tsx)
    const getScoreLabel = (score: number) => {
        if (score == null) return '-';
        if (score >= 3.9) return 'S';
        if (score >= 3.4) return 'A';
        if (score >= 2.9) return 'B';
        if (score >= 1.9) return 'C';
        if (score >= 1.4) return 'D';
        
        return 'F';
    };

    const getGradeBackgroundColor = (grade: number) => {
        const label = getScoreLabel(grade);
        const gradeColors = {
            S: 'bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 text-amber-900 shadow-lg border border-amber-300',
            A: 'bg-green-300 text-green-900',
            B: 'bg-green-200 text-green-700',
            C: 'bg-yellow-200 text-yellow-700',
            D: 'bg-red-200 text-red-700',
            F: 'bg-red-500 text-red-900',
        };
        return gradeColors[label] || 'bg-gray-100 text-gray-800';
    };

    const getGradeTextColor = (grade: number) => {
        const label = getScoreLabel(grade);
        const gradeTextColors = {
            S: 'text-amber-900 font-bold drop-shadow-sm',
            A: 'text-green-900',
            B: 'text-green-700',
            C: 'text-yellow-700',
            D: 'text-red-700',
            F: 'text-red-900',
        };
        return gradeTextColors[label];
    };

    // Polling for grading pitches
    useEffect(() => {
        if (demoMode) return;
        const gradingPitchIds = pitches
            .filter(pitch => pitch.status === 'grading')
            .map(pitch => pitch.id);
        
        setGradingPitches(new Set(gradingPitchIds));

        if (gradingPitchIds.length > 0) {
            const interval = setInterval(async () => {
                try {
                    const updatedPitches = await api.getPitchAnalyses();
                    setPitches(updatedPitches);
                } catch (error) {
                    console.error('Failed to poll pitch status:', error);
                }
            }, 5000); // Poll every 5 seconds

            return () => clearInterval(interval);
        }
    }, [pitches, api]);

    // Grading wait component (similar to InterviewGradingWait)
    const renderGradingWait = () => (
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 animate-fade-in">
            <div className="bg-white dark:bg-card rounded-2xl shadow-xl p-6 sm:p-12 max-w-2xl w-full flex flex-col items-center">
                <h1 className="flex items-center gap-2 text-lg sm:text-xl text-primary font-semibold mb-2 text-center">
                    We're analyzing your pitch
                </h1>
                <img src={logo} alt="HiredCoach Logo" className="w-20 sm:w-28 mb-4" />
                <div className="flex items-center gap-2 text-lg sm:text-xl text-primary font-semibold mb-2 text-center">
                    This might take a minute...
                </div>
                <div className="flex flex-col items-center">
                    <svg className="animate-spin w-8 sm:w-12 h-8 sm:h-12 text-primary mb-2" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                    <span className="text-xs sm:text-sm text-muted-foreground">Grading in progress...</span>
                </div>
                <div className="text-center text-muted-foreground mb-2 text-sm sm:text-base space-y-2 mt-6">
                    We're analyzing your pitch against your resume and best practices; we're giving you:
                </div>
                <ul className="text-left text-muted-foreground mb-4 text-sm sm:text-lg list-disc list-inside space-y-2 mt-6">
                    <li>Detailed feedback on clarity, structure, and relevance</li>
                    <li>Specific coaching tips to improve your delivery</li>
                    <li>A breakdown of your strengths and areas for improvement</li>
                </ul>
            </div>
        </div>
    );

    // Score breakdown component for completed pitches
    const renderScoreBreakdown = (pitch: PitchAnalysisResponse) => {
        // Check if scores are available
        const hasScores = pitch.overall_score !== undefined && pitch.overall_score !== null &&
                         pitch.clarity_score !== undefined && pitch.clarity_score !== null &&
                         pitch.structure_score !== undefined && pitch.structure_score !== null &&
                         pitch.relevance_score !== undefined && pitch.relevance_score !== null;

        return (
            <Tabs defaultValue="summary" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="summary" className="text-xs sm:text-sm">Summary</TabsTrigger>
                    <TabsTrigger value="scores" className="text-xs sm:text-sm flex items-center gap-1" disabled={!hasScores}>
                        <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4" />
                        Scores
                    </TabsTrigger>
                </TabsList>
                <TabsContent value="summary" className="mt-3">
                    <div className="p-3 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                        <p className="text-sm text-blue-900 font-medium">
                            {pitch.summary && pitch.summary.length > 100 
                                ? `${pitch.summary.substring(0, 100)}...` 
                                : pitch.summary || "Analysis completed successfully."
                            }
                        </p>
                    </div>
                </TabsContent>
                {hasScores && (
                    <TabsContent value="scores" className="mt-3">
                        <div className="space-y-3">
                            <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium">Clarity</span>
                                    <div className="group relative">
                                        <HelpCircle className="w-3 h-3 text-gray-400 cursor-help" />
                                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                            How clear and easy to understand your pitch was
                                        </div>
                                    </div>
                                </div>
                                <div className={`px-2 py-1 rounded text-xs font-semibold ${getGradeBackgroundColor(pitch.clarity_score)}`}>
                                    {getScoreLabel(pitch.clarity_score)} ({pitch.clarity_score.toFixed(1)})
                                </div>
                            </div>
                            <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium">Structure</span>
                                    <div className="group relative">
                                        <HelpCircle className="w-3 h-3 text-gray-400 cursor-help" />
                                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                            How well organized your pitch was (past → present → future)
                                        </div>
                                    </div>
                                </div>
                                <div className={`px-2 py-1 rounded text-xs font-semibold ${getGradeBackgroundColor(pitch.structure_score)}`}>
                                    {getScoreLabel(pitch.structure_score)} ({pitch.structure_score.toFixed(1)})
                                </div>
                            </div>
                            <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium">Relevance</span>
                                    <div className="group relative">
                                        <HelpCircle className="w-3 h-3 text-gray-400 cursor-help" />
                                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                            How well your pitch aligned with your resume and goals
                                        </div>
                                    </div>
                                </div>
                                <div className={`px-2 py-1 rounded text-xs font-semibold ${getGradeBackgroundColor(pitch.relevance_score)}`}>
                                    {getScoreLabel(pitch.relevance_score)} ({pitch.relevance_score.toFixed(1)})
                                </div>
                            </div>
                            <div className="border-t pt-3 mt-3">
                                <div className="flex items-center justify-between p-2 bg-blue-50 rounded font-semibold">
                                    <span className="text-sm">Overall Score</span>
                                    <div className={`px-3 py-1 rounded text-sm font-bold ${getGradeBackgroundColor(pitch.overall_score)}`}>
                                        {getScoreLabel(pitch.overall_score)} ({pitch.overall_score.toFixed(1)})
                                    </div>
                                </div>
                            </div>
                        </div>
                    </TabsContent>
                )}
            </Tabs>
        );
    };

    if (loading) {
        const loadingContent = (
            <div className="container mx-auto px-4 py-8">
                <div className="animate-pulse">
                    <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
                    <div className="space-y-4">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
                        ))}
                    </div>
                </div>
            </div>
        );
        return embed ? loadingContent : (
            <DashboardLayout>{loadingContent}</DashboardLayout>
        );
    }

    // Check if there are any grading pitches to show wait screen
    const hasGradingPitches = pitches.some(pitch => pitch.status === 'grading');
    
    if (hasGradingPitches && !demoMode) {
        return embed ? renderGradingWait() : (
            <DashboardLayout>
                {renderGradingWait()}
            </DashboardLayout>
        );
    }

    const main = (
            <div className={`container mx-auto ${embed ? 'px-0 py-4' : 'px-4 sm:px-6 lg:px-8 py-6 sm:py-8'}`}>
                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                            Pitch Practice
                        </h1>
                        <p className="text-gray-600 text-sm sm:text-base">
                            Practice and perfect your elevator pitch about <b>you</b>. Our AI Coach will help you learn how to convey how awesome you are.
                        </p>
                    </div>
                    {!demoMode && (
                        <Button 
                            onClick={handleCreatePitch}
                            className="w-full sm:w-auto flex items-center gap-2"
                        >
                            <Plus className="w-4 h-4" />
                            New Pitch
                        </Button>
                    )}
                </div>

                {/* Pitch List */}
                {pitches.length === 0 ? (
                    <Card className="text-center py-12">
                        <CardContent>
                            <div className="bg-gray-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                                <MessageCircle className="w-10 h-10 text-gray-400" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No Pitches Yet</h3>
                            <p className="text-gray-600 mb-6 max-w-md mx-auto">
                                Start practicing the "you" elevator pitch and learn to efficiently tell <b>your</b> story. The Coach will help you finetune it maximize that first impression.
                            </p>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="space-y-6">
                        {pitches.map((pitch) => (
                            <Card 
                                key={pitch.id} 
                                className="hover:shadow-lg transition-all duration-200 cursor-pointer border-l-4 border-l-blue-400 hover:border-l-blue-600 overflow-hidden"
                                onClick={() => handlePitchClick(pitch.id)}
                            >
                                <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 pb-4">
                                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                        <div className="flex-1">
                                            <CardTitle className="text-xl sm:text-2xl flex items-center gap-3 text-gray-800">
                                                <Clock className="w-6 h-6 text-blue-600" />
                                                {getTimeDisplay(pitch.time_limit)} Pitch
                                            </CardTitle>
                                            <CardDescription className="text-sm text-gray-600 mt-2 font-medium">
                                                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                                    <span>{formatDate(pitch.created_at)}</span>
                                                    <span className="hidden sm:inline text-gray-400">•</span>
                                                    <Badge variant="outline" className="w-fit text-xs">
                                                        Focus: {getFocusDisplay(pitch)}
                                                    </Badge>
                                                    {pitch.job_id && (
                                                        <>
                                                            <span className="hidden sm:inline text-gray-400">•</span>
                                                            <JobNameDisplay jobId={pitch.job_id} demoMode={demoMode} />
                                                        </>
                                                    )}
                                                </div>
                                            </CardDescription>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            {getStatusBadge(pitch)}
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="pt-6 hidden sm:block">
                                    <div className="bg-gray-50 rounded-lg p-4 mb-4">
                                        <h4 className="font-semibold text-gray-900 mb-2 text-sm">Your Pitch:</h4>
                                        <div className="flex items-start gap-3">
                                            <UserAvatar user={user} className="h-10 w-10 border-0 mt-0.5" />
                                            <p className="text-gray-700 text-sm leading-relaxed italic">
                                                "{pitch.content.length > 200 
                                                    ? `${pitch.content.substring(0, 200)}...` 
                                                    : pitch.content
                                                }"
                                            </p>
                                        </div>
                                    </div>
                                    {(pitch.status === 'complete' || pitch.status === 'ready') && pitch.summary && (
                                        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg p-4 border-l-4 border-blue-500">
                                            <div className="flex items-start gap-3">
                                                <MessageCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                                                <div>
                                                    <h4 className="font-semibold text-blue-900 mb-1 text-sm">Assessment Summary</h4>
                                                    <p className="text-blue-800 text-sm leading-relaxed">
                                                        {pitch.summary.length > 150 
                                                            ? `${pitch.summary.substring(0, 150)}...` 
                                                            : pitch.summary
                                                        }
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </div>
    );

    return embed ? main : (
        <DashboardLayout>
            {main}
        </DashboardLayout>
    );
};

export default Pitch;
